import { assertNever } from "@msys/common";
import { CardContainer } from "@msys/ui";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ApplyItemActionInput } from "../../../../clients/graphqlTypes.js";
import { PlaceholderSelection } from "../../doc-items/selection/PlaceholderSelection.js";
import { noopRefetchWizardQuestions } from "../../doc-items/withWizardRefetch.js";
import { PlaceholderSelection_ItemFragment } from "../../doc-items/selection/PlaceholderSelection.generated.js";
import { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";
import { getItemQuestionPosition, getQuestionPrompt } from "./questionShell.js";
import { WizardRendererRef } from "./WizardRendererProps.js";
import { WizardContingencyRenderer } from "./WizardContingencyRenderer.js";
import { WizardDecisionRenderer } from "./WizardDecisionRenderer.js";
import { WizardPropertiesRenderer } from "./WizardPropertiesRenderer.js";
import { WizardRecommendationsRenderer } from "./WizardRecommendationsRenderer.js";

interface Props {
  projectId: string;
  quoteId: string;
  questions: Array<WizardQuestions_QuestionFragment>;
  question: WizardQuestions_QuestionFragment | null;
  /**
   * The item the URL selects, shown in the idle state when it has no question.
   * No selection is the done state: nothing is left to answer in this mode.
   */
  selectedItem: { id: string; title: string } | null;
  /** True while the document or the question list is still being read. */
  loading: boolean;
  /** True when the document or the question list failed to load. */
  loadError: boolean;
  /** The document item behind the current question, for the placeholder panels. */
  placeholderItem: PlaceholderSelection_ItemFragment | null;
  expandedItemIds: string[] | undefined;
  onAnswer: (questionId: string, action: ApplyItemActionInput) => Promise<void>;
  onApplied: (questionId: string) => Promise<void>;
  busy: boolean;
  /**
   * Handed to a renderer that stages its answer, so the action bar's forward
   * button can submit it.
   */
  stagedRef: React.RefObject<WizardRendererRef>;
}

/**
 * The right column: one question at a time, with its prompt and its position
 * within the item. The renderer per kind is the only thing that varies; the
 * navigation lives in the page's action bar.
 */
export const WizardQuestionShell = ({
  projectId,
  quoteId,
  questions,
  question,
  selectedItem,
  loading,
  loadError,
  placeholderItem,
  expandedItemIds,
  onAnswer,
  onApplied,
  busy,
  stagedRef,
}: Props) => {
  const { t } = useTranslate(["Global", "Quote", "QuoteItem"]);

  const position = question
    ? getItemQuestionPosition(questions, question.questionId)
    : null;

  const answer = React.useCallback(
    async (action: ApplyItemActionInput) => {
      if (question) await onAnswer(question.questionId, action);
    },
    [onAnswer, question]
  );

  return (
    <CardContainer
      title={
        question
          ? getQuestionPrompt(question)
          : (selectedItem?.title ??
            (loading || loadError
              ? t("Wizard", { ns: "Quote" })
              : t("No further open questions", { ns: "Quote" })))
      }
      isExpandable={false}
    >
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        {position && position.total > 1 && (
          <Typography variant="caption" color="text.secondary">
            {t("Question {index} of {total}", {
              ns: "Quote",
              index: position.index,
              total: position.total,
            })}
          </Typography>
        )}

        <Box>
          {question ? (
            <QuestionRenderer
              key={question.questionId}
              projectId={projectId}
              quoteId={quoteId}
              question={question}
              placeholderItem={placeholderItem}
              expandedItemIds={expandedItemIds}
              onAnswer={answer}
              onApplied={() => onApplied(question.questionId)}
              disabled={busy}
              stagedRef={stagedRef}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {loadError
                ? t("The wizard questions could not be loaded", { ns: "Quote" })
                : selectedItem
                  ? t("This item has no open questions", { ns: "Quote" })
                  : loading
                    ? t("Select an item to see its questions", { ns: "Quote" })
                    : t(
                        "Every question in this wizard mode has been answered",
                        {
                          ns: "Quote",
                        }
                      )}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContainer>
  );
};

const QuestionRenderer = ({
  projectId,
  quoteId,
  question,
  placeholderItem,
  expandedItemIds,
  onAnswer,
  onApplied,
  disabled,
  stagedRef,
}: {
  projectId: string;
  quoteId: string;
  question: WizardQuestions_QuestionFragment;
  placeholderItem: PlaceholderSelection_ItemFragment | null;
  expandedItemIds: string[] | undefined;
  onAnswer: (action: ApplyItemActionInput) => Promise<void>;
  onApplied: () => Promise<void>;
  disabled: boolean;
  stagedRef: React.RefObject<WizardRendererRef>;
}) => {
  switch (question.__typename) {
    case "WizardProductQuestion":
    case "WizardPlaceholderForTemplateQuestion": {
      // The panels persist through their own mutations; the shell only treats
      // a fill as the answer signal and steps on it, and the step refetches.
      return placeholderItem ? (
        <PlaceholderSelection
          key={`${placeholderItem.id}:${question.__typename}`}
          kind={
            question.__typename === "WizardProductQuestion"
              ? "product"
              : "template"
          }
          projectId={projectId}
          docId={quoteId}
          item={placeholderItem}
          expandedItemIds={expandedItemIds}
          refetchWizardQuestions={noopRefetchWizardQuestions}
          handleClose={() => {}}
          onApplied={onApplied}
        />
      ) : null;
    }
    case "WizardDecisionQuestion": {
      return (
        <WizardDecisionRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    case "WizardMultiplePropertiesQuestion": {
      return (
        <WizardPropertiesRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    case "WizardContingencyQuestion": {
      return (
        <WizardContingencyRenderer
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    case "WizardMultipleRecommendationsQuestion": {
      return (
        <WizardRecommendationsRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    default: {
      return assertNever(question);
    }
  }
};
